import api from "@/api";

const defaultArticle = {
  title: "",
  slug: "",
  default_image_id: null,
  elements: [],
  publish_date: new Date(),
  categories: [],
  tags: [],
  status: "pending",
  banner_id: null,
  banner: {},
  is_homepage_banner: false,
};

export default {
  namespaced: true,

  state: {
    articles: [],
    errorState: {},
    currentArticle: defaultArticle,
    featured: [],
    gridLayout: true,
    filters: {
      searchQuery: "",
      categories: [],
      size: null,
      tags: [],
      isTrust: false,
      published: false,
      tabFilter: ["approved", "published"],
    },
    // tabFilter: '',
    sortBy: {
      field: "created_time",
      direction: "desc",
    },
    pagination: {
      total: 12,
      per_page: 12,
      current_page: 1,
      last_page: 1,
      from: 1,
      to: 12,
    },
  },
  mutations: {
    SET_ARTICLES(state, payload) {
      state.articles = payload;
    },
    SET_ERROR(state, payload) {
      state.errorState = payload;
    },
    SET_ARTICLE(state, currentArticle) {
      state.currentArticle = Object.assign({}, currentArticle);
    },
    RESET_ARTICLE(state) {
      state.currentArticle = JSON.parse(JSON.stringify(defaultArticle));
    },
    SET_GRID_LAYOUT(state, layout) {
      state.gridLayout = layout;
    },
    SET_BANNER_ID(state, id) {
      state.currentArticle.default_image_id = id;
    },
    SET_RESULT_COUNT(state, number) {
      state.filters.size = number;
      state.pagination.per_page = number;
      state.pagination.total = number;
      state.pagination.to = number;
    },
    SET_FEATURED(state, articles) {
      state.featured = articles;
    },
    SET_TRUST_FILTER(state, enabled) {
      state.filters.isTrust = enabled;
    },
    SET_PUBLISHED_FILTER(state, enabled) {
      state.filters.published = enabled;
    },
    RESET_FILTERS(state) {
      state.filters = {
        searchQuery: "",
        categories: [],
        size: null,
        tags: [],
        isTrust: false,
        published: false,
        tabFilter: ["approved", "published"],
      };
    },
    SET_FILTER_TOTAL(state, total) {
      state.pagination.total = total;
      state.pagination.last_page = Math.ceil(total / state.pagination.per_page);
    },
    SET_CURRENT_PAGE(state, page) {
      state.pagination.current_page = page;
    },
    // not constant to match naming conventions used elsewhere
    setSearchQuery(state, payload) {
      state.filters.searchQuery = payload;
    },
    setTabFilter(state, tabFilter) {
      state.filters.tabFilter = tabFilter;
    },
    setFilterCategories(state, categories) {
      state.filters.categories = categories;
    },
    setPage(state, page) {
      state.pagination.current_page = page;
    },
  },
  actions: {
    /**
     * Get all articles
     */
    async list({ commit, state }, options) {
      const localOptions = { ...options };
      const page = state.pagination.current_page;
      let isFull = false;

      if (!("includeWeb" in localOptions)) {
        localOptions.includeWeb = true;
      }
      if (!("isFull" in localOptions)) {
        isFull = localOptions.isFull;
      }

      const size = state.pagination.per_page;
      let from = size * page - size;

      if (!from) {
        from = 0;
      }
      const filter = {
        size,
        from,
        sort: [
          {
            [state.sortBy.field]: {
              order: state.sortBy.direction,
            },
          },
        ],
        query: {
          bool: {
            must: [
              {
                bool: {
                  should: [
                    {
                      match: {
                        tag_names: {
                          query: state.filters.searchQuery,
                          operator: "and",
                          zero_terms_query: "all",
                          fuzziness: "AUTO",
                        },
                      },
                    },
                    {
                      bool: {
                        should: [
                          {
                            match: {
                              title: {
                                query: state.filters.searchQuery,
                                operator: "and",
                                zero_terms_query: "all",
                                fuzziness: "AUTO",
                              },
                            },
                          },
                          {
                            match_phrase_prefix: {
                              title: {
                                query: state.filters.searchQuery,
                                slop: 50,
                              },
                            },
                          },
                        ],
                      },
                    },
                  ],
                },
              },
            ],
          },
        },
      };

      if (state.filters.size) {
        filter.size = state.filters.size;
      }

      if (state.filters.categories.length >= 1) {
        filter.query.bool.must.push({
          terms: {
            categories: state.filters.categories,
          },
        });
      }

      if (state.filters.isTrust) {
        filter.query.bool.must.push({
          term: {
            interestgroups: 13, // TODO: This needs to match the MFT Trust group
          },
        });
      }

      if (state.filters.published) {
        filter.query.bool.must.push({
          terms: {
            status: ["published", "approved"],
          },
        });
      }

      if (state.filters.tabFilter && !state.filters.published) {
        filter.query.bool.must.push({
          terms: {
            status: state.filters.tabFilter,
          },
        });
      }

      let resp;

      let url = "/news/articles/flat";

      // is flat by default
      if (localOptions.isFull) {
        url = `/news/articles`;
      }

      try {
        resp = await api().put(url, { filter });
        commit("SET_ARTICLES", resp.data.data.article);
        commit("SET_FILTER_TOTAL", resp.data.meta.filter_total);
        commit("SET_CURRENT_PAGE", page);
        return Promise.resolve(resp.data.data.article);
      } catch (error) {
        // If there are no articles it returns a 404, so set the response here
        if (error.response.data.data.length < 1) {
          commit("SET_ARTICLES", []);
          return Promise.resolve([]);
        }
        commit(
          "SET_ERROR",
          error.status
            ? error.status
            : "Sorry, there was an error.  Please try again"
        );
        return Promise.reject(error);
      }
    },

    /**
     * Basically the same as list, but allows us to pass a specific query
     * TODO:  Need to include the slop/match stuff from above, but that should
     * come from the query...
     */
    async query({ commit }, { query = {}, includeWeb = false }) {
      let resp;

      let url = "/news/articles/flat";

      try {
        resp = await api().put(url, { filter: query });
        commit("SET_ARTICLES", resp.data.data.article);
        commit("SET_FILTER_TOTAL", resp.data.meta.filter_total);
        commit("SET_CURRENT_PAGE", 1);
        return Promise.resolve(resp.data.data.article);
      } catch (error) {
        // If there are no articles it returns a 404, so set the response here
        if (!error.data || error.data.length < 1) {
          commit("SET_ARTICLES", []);
          return Promise.resolve([]);
        }
        commit(
          "SET_ERROR",
          error.status
            ? error.status
            : "Sorry, there was an error.  Please try again"
        );
        return Promise.reject(error);
      }
    },

    /**
     * Get a specific article
     */
    async read({ commit }, id) {
      let resp;
      try {
        resp = await api().get(`/news/article/${id}`);
        const returnArticle = {
          ...resp.data.data.article[0],
        };
        commit("SET_ARTICLE", returnArticle);
        return Promise.resolve(returnArticle);
      } catch (err) {
        commit(
          "SET_ERROR",
          err.status
            ? err.status
            : "Sorry, there was an error.  Please try again"
        );
        return Promise.reject(err.response.data);
      }
    },

    /**
     * Get the elements for a specific article
     */
    async fetchElements(context, id) {
      let resp;

      try {
        resp = await api().get(`/news/article/${id}/elements`);
        return Promise.resolve(resp.data.data.element);
      } catch (err) {
        return Promise.resolve([]);
      }
    },

    /**
     * Create a new article
     */
    create({ dispatch, state, rootGetters }, pageData) {
      const pubData = pageData || Object.assign({}, state.currentPage);
      pubData.title = pageData.title || "Untitled Page";
      pubData.elements = rootGetters["pageElements/forServer"] || null;

      return new Promise((resolve, reject) => {
        api()
          .post("/news/article", { article: pubData })
          .then((page) => {
            dispatch(
              "notifications/create",
              {
                body: "The article was created",
                type: "success",
              },
              { root: true }
            );
            // clear the local storage item for banners
            localStorage.removeItem("mft_store_getFixedBanner");
            resolve(page);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    /**
     * Update a news article
     */
    update({ state, commit, dispatch, rootGetters }, { pageId, pageData }) {
      const pubData = pageData || Object.assign({}, state.currentArticle);
      pubData.title = pageData.title || "Untitled Page";
      pubData.elements = rootGetters["pageElements/forServer"] || null;

      return new Promise((resolve, reject) => {
        api()
          .put(`/news/article/${pageId}`, { article: pubData })
          .then((page) => {
            commit("SET_ARTICLE", page.data.data.article[0]);
            dispatch(
              "notifications/create",
              {
                body: "The article was updated",
                type: "success",
              },
              { root: true }
            );
            // clear the local storage item for banners
            localStorage.removeItem("mft_store_getFixedBanner");
            resolve(page);
          })
          .catch((error) => {
            if (error.error.status_code === 400) {
              dispatch(
                "notifications/create",
                {
                  title: "Error!",
                  body: error.error.message,
                  type: "danger",
                },
                { root: true }
              );
            }
            reject(error);
          });
      });
    },

    /**
     * Resubmit an article - update it first, then go through resubmit
     * make sure we have a response from the resubmit before resolving
     */
    async resubmit({ dispatch }, { pageId, pageData }) {
      let response;

      try {
        await dispatch("update", { pageId, pageData });
        response = await api().put(`/news/article/${pageId}/resubmit`);
        return Promise.resolve(response.data.data.article[0]);
      } catch (err) {
        console.log(...err);
        return Promise.reject(new Error(err));
      }
    },

    /**
     * Approve an article
     */
    async approve({ dispatch }, id) {
      let response;

      try {
        response = await api().put(`/news/article/${id}/approve`);
        dispatch("read", id);
        localStorage.removeItem("mft_store_getFixedBanner");
        return Promise.resolve(response.data.data.article);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    /**
     * Reject an article
     */
    async reject({ dispatch }, params) {
      let response;

      try {
        response = await api().put(`/news/article/${params.id}/reject`, {
          reject: { reason: params.reason },
        });
        dispatch("list");
        dispatch("read", params.id);
        return Promise.resolve(response.data.data.article);
      } catch (err) {
        return Promise.reject(err);
      }
    },

    /**
     * Delete an article
     */
    delete({ commit, dispatch }, id) {
      api()
        .delete(`/news/article/${id}`)
        .then(() => {
          dispatch("list");
          dispatch(
            "notifications/create",
            {
              body: "The article was deleted",
              type: "success",
            },
            { root: true }
          );
        })
        .catch((err) => {
          /**
           * In the case we have deleted the last item in the list
           */
          dispatch("list");
          commit("SET_ERROR", err);
        });
    },

    /**
     * Set a news article to be featured
     */
    async setFeatured({ commit, dispatch }, id) {
      let resp;

      try {
        resp = await api().put(`/news/article/${id}/addhomepagebanner`);
        dispatch("getFixedBanner", null, { root: true });
        return Promise.resolve(resp);
      } catch (err) {
        commit("SET_ERROR", err);
        return Promise.resolve(err);
      }
    },

    /**
     * Remove featured story
     */
    async removeFeatured({ commit, dispatch }, id) {
      let resp;

      try {
        resp = await api().put(`/news/article/${id}/removehomepagebanner`);
        dispatch("getFixedBanner", null, { root: true });
        return Promise.resolve(resp);
      } catch (err) {
        commit("SET_ERROR", err);
        return Promise.resolve(err);
      }
    },

    /**
     * Get all featured articles
     */
    async getFeatured({ commit }) {
      let resp;

      try {
        resp = await api().get("/homepage/banner");
        commit("SET_FEATURED", resp.data.data.article);
        return Promise.resolve(resp.data.data.article);
      } catch (err) {
        commit("SET_ERROR", err);
        return Promise.resolve(err);
      }
    },

    /**
     * determine if the news index page displays in a grid or a list
     *
     */
    toggleGrid({ commit }, value) {
      commit("SET_GRID_LAYOUT", value);
    },
  },
  getters: {
    getFeaturedNewsId: (state) => state.featured.map((article) => article.id),
  },
};
